import { memo, useMemo } from 'react';
import styled from 'styled-components';
import { rgba } from 'polished';

import { cA568FE, cEA135E, cF1E20D } from '#/colors';

import { Loadable } from '#/components/shared/Loadable';
import { StarknetTxLink } from '#/components/shared/TxLink';
import Tooltip, { HelpUnderline } from '#/components/shared/Tooltip';

import { FillView } from '#/features/perpetuals/trade-overview/fill-view';
import { useFillsView } from '#/features/perpetuals/trade-overview/fills-context';
import { useTranslation } from '#/features/localization/utils';

import maskString from '#/utils/maskString';
import copyToClipboard from '#/utils/copyToClipboard';

import { Empty, Error, Mono, SideBuy, SideSell, ValuePair } from './atoms';
import { TableSpec, createTradeOverviewTable } from './table';

interface Props {
  tableSpec: TableSpec<string>;
}

export default function TradeHistory(props: Props) {
  const { tableSpec } = props;
  const [Table, Row] = useMemo(
    () => createTradeOverviewTable(tableSpec),
    [tableSpec],
  );
  const { status, error, fills } = useFillsView();
  const { t } = useTranslation();

  if (status === 'error') {
    return <Error>{error}</Error>;
  }

  const isEmpty = fills.length === 0;
  const isLoading = status === 'loading' || status === 'disconnected';

  if (isEmpty) {
    return (
      <Loadable isLoading={isLoading}>
        <Empty>{t('You have no trades')}</Empty>
      </Loadable>
    );
  }

  return (
    <Loadable isLoading={isLoading}>
      <Table>
        {fills.map((fill) => (
          <Row
            key={fill.id}
            status={
              <FillStatus value={fill.status.value} label={fill.status.label} />
            }
            time={
              <ValuePair
                primary={fill.createdTime}
                secondary={fill.createdDate}
              />
            }
            market={fill.market}
            side={sideComponent[fill.side]}
            price={fill.price}
            amount={fill.size}
            total={
              <>
                <Mono>{fill.priceTotal}</Mono>
                <br />
                <Mono css="opacity: 0.7">{fill.fee}</Mono>
              </>
            }
            liquidity={fill.liquidity}
            txHash={<StarknetTxLink hash={fill.txHash} />}
            id={
              <Id onClick={() => copyToClipboard(fill.id)}>
                {maskString(fill.id, 4, 4)}
              </Id>
            }
          />
        ))}
      </Table>
    </Loadable>
  );
}

function FillStatus(props: FillView['status']) {
  switch (props.value) {
    case 'PENDING':
      return <StatusPending>{props.label}</StatusPending>;
    case 'BUST':
      return <StatusBust>{props.label}</StatusBust>;
    default:
      return <span>{props.label}</span>;
  }
}

const StatusPending = memo(function StatusPending(props: {
  readonly children: string;
}) {
  return (
    <span>
      <Circle color={cF1E20D} /> {props.children}
    </span>
  );
});

const StatusBust = memo(function StatusBust(props: {
  readonly children: string;
}) {
  return (
    <Tooltip label="Trade failed to settle, please re-try. Working on preventing recurrence.">
      <span
        css={`
          color: ${rgba(cEA135E, 0.7)};
        `}
      >
        <Triangle color={cEA135E} />{' '}
        <HelpUnderline>{props.children}</HelpUnderline>
      </span>
    </Tooltip>
  );
});

const sideComponent = {
  BUY: <SideBuy />,
  SELL: <SideSell />,
};

const Id = styled.span`
  &:hover {
    cursor: pointer;
    color: ${cA568FE};
  }
`;

const Circle = styled.span<{
  readonly color: string;
}>`
  background-color: ${(props) => props.color};
  border-radius: 50%;
  display: inline-block;
  height: 0.3em;
  margin: 0.16em 0;
  width: 0.3em;
`;

const Triangle = styled.span<{ readonly color: string }>`
  display: inline-block;
  position: relative;
  background-color: ${(props) => props.color};
  text-align: left;
  margin: 0.16em;

  &:before,
  &:after {
    content: '';
    position: absolute;
    background-color: inherit;
  }
  &,
  &:before,
  &:after {
    width: 4px;
    height: 4px;
    border-top-right-radius: 50%;
  }

  & {
    transform: rotate(-60deg) skewX(-30deg) scale(1, 0.866);
  }
  &:before {
    transform: rotate(-135deg) skewX(-45deg) scale(1.414, 0.707)
      translate(0, -50%);
  }
  &:after {
    transform: rotate(135deg) skewY(-45deg) scale(0.707, 1.414) translate(50%);
  }
`;
